import { CustomTooltip } from "@shared/components/CustomTooltip";
import { useCurrency } from "@shared/selectors/globalSelectors";
import { Card, CardContent, CardHeader, CardTitle } from "@shared/ui/card";
import { CheckCircle, DollarSign, Gauge } from "lucide-react";

interface PerformanceMetricsSectionProps {
  data: any;
}

export const PerformanceMetricsSection = ({
  data,
}: PerformanceMetricsSectionProps) => {
  const velocityPerformance = data?.velocityPerformance;
  const qualityCompliance = data?.qualityCompliance;
  const costResources = data?.costResources;

  const getMetricColor = (
    value: number,
    goodThreshold: number,
    poorThreshold: number,
    reverse = false
  ) => {
    if (reverse) {
      if (value <= goodThreshold) return "text-status-good";
      if (value <= poorThreshold) return "text-status-fair";
      return "text-status-poor";
    } else {
      if (value >= goodThreshold) return "text-status-good";
      if (value >= poorThreshold) return "text-status-fair";
      return "text-status-poor";
    }
  };

  const currency = useCurrency();

  return (
    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
      <Card>
        <CardHeader>
          <div className="flex items-center gap-2">
            <CardTitle className="flex items-center gap-2">
              <Gauge className="h-4 w-4" />
              Velocity & Performance
            </CardTitle>
            <CustomTooltip message="Key performance indicators measuring the speed and efficiency of maintenance operations including response times and completion rates." />
          </div>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <div>
              <p className="text-sm text-muted-foreground">
                First-Time Fix Rate
              </p>
              <p
                className={`text-xl font-bold ${getMetricColor(
                  velocityPerformance?.firstTimeFixRate.value,
                  85,
                  75
                )}`}
              >
                {velocityPerformance?.firstTimeFixRate.value}%
              </p>
            </div>
            <div>
              <p className="text-sm text-muted-foreground">MTTR</p>
              <p
                className={`text-xl font-bold ${getMetricColor(
                  velocityPerformance?.mttr?.value,
                  4,
                  6,
                  true
                )}`}
              >
                {velocityPerformance?.mttr?.value}h
              </p>
            </div>
            <div>
              <p className="text-sm text-muted-foreground">MTBF</p>
              <p
                className={`text-xl font-bold ${getMetricColor(
                  velocityPerformance?.mtbf.value,

                  600,
                  400
                )}`}
              >
                {velocityPerformance?.mttr?.value}h
              </p>
            </div>
            <div>
              <p className="text-sm text-muted-foreground">Scheduled %</p>
              <p
                className={`text-xl font-bold ${getMetricColor(
                  velocityPerformance?.scheduled?.value || 0,
                  75,
                  60
                )}`}
              >
                {data?.scheduledVsUnscheduled ?? "NA"}%
              </p>
            </div>
          </div>

          <div className="border-t pt-3">
            <div className="grid grid-cols-2 gap-4 text-sm">
              <div className="flex justify-between">
                <span>Response Time:</span>
                <span
                  className={`font-medium ${getMetricColor(
                    velocityPerformance?.responseTime?.value,
                    2,
                    4,
                    true
                  )}`}
                >
                  {velocityPerformance?.responseTime?.value}h
                </span>
              </div>
              <div className="flex justify-between">
                <span>Close Time:</span>
                <span
                  className={`font-medium ${getMetricColor(
                    velocityPerformance?.closeTime?.value,
                    12,
                    24,
                    true
                  )}`}
                >
                  {velocityPerformance?.closeTime?.value}h
                </span>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <div className="flex items-center gap-2">
            <CardTitle className="flex items-center gap-2">
              <CheckCircle className="h-4 w-4" />
              Quality & Compliance
            </CardTitle>
            <CustomTooltip message="Metrics tracking the quality of maintenance work and adherence to compliance requirements and safety standards." />
          </div>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <div>
              <p className="text-sm text-muted-foreground">SLA Achievement</p>
              <p
                className={`text-xl font-bold ${getMetricColor(
                  qualityCompliance?.slaAchievement?.value,
                  95,
                  85
                )}`}
              >
                {qualityCompliance?.slaAchievement?.value}%
              </p>
            </div>
            <div>
              <p className="text-sm text-muted-foreground">PM Compliance</p>
              <p
                className={`text-xl font-bold ${getMetricColor(
                  qualityCompliance?.pmCompliance?.value,
                  90,
                  80
                )}`}
              >
                {qualityCompliance?.pmCompliance?.value}%
              </p>
            </div>
            <div>
              <p className="text-sm text-muted-foreground">Rework Rate</p>
              <p
                className={`text-xl font-bold ${getMetricColor(
                  qualityCompliance?.reworkRate?.value,
                  5,
                  10,
                  true
                )}`}
              >
                {qualityCompliance?.reworkRate?.value}%
              </p>
            </div>
            <div></div>
          </div>

          <div className="border-t pt-3">
            <div className="flex justify-between items-center text-sm">
              <span>Unplanned Maintenance:</span>
              <span
                className={`font-bold ${getMetricColor(
                  qualityCompliance?.unplannedMaintenance?.value,

                  20,
                  30,
                  true
                )}`}
              >
                {qualityCompliance?.unplannedMaintenance?.value}%
              </span>
            </div>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <div className="flex items-center gap-2">
            <CardTitle className="flex items-center gap-2">
              <DollarSign className="h-4 w-4" />
              Cost & Resources
            </CardTitle>
            <CustomTooltip message="Financial and resource utilization metrics including labor costs, technician efficiency, and emergency spending." />
          </div>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <div>
              <p className="text-sm text-muted-foreground">Avg Cost/SO</p>
              <p className="text-xl font-bold">
                {currency} {costResources?.avgCostWo?.value}
              </p>
            </div>
            <div>
              <p className="text-sm text-muted-foreground">Labor Hours</p>
              <p className="text-xl font-bold">
                {costResources?.laborHours?.value}h
              </p>
            </div>

            <div>
              <p className="text-sm text-muted-foreground">Asset Util</p>
              <p
                className={`text-xl font-bold ${getMetricColor(
                  costResources?.assetAvailability?.value,
                  85,
                  75
                )}`}
              >
                {costResources?.assetAvailability?.value}%
              </p>
            </div>
          </div>

          <div className="border-t pt-3">
            <div className="flex justify-between items-center text-sm">
              <span>Emergency Costs:</span>
              <span className="font-bold text-orange-600">
                {currency} {costResources?.emergencyCosts?.value}
              </span>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};
